<template>
  <div v-if="isMount" class="base-modal">
    <Teleport :to="modalSettings.to">
      <Transition
        appear
        :name="modalSettings.animation"
        @before-enter="beforeEnter()"
        @after-enter="afterEnter()"
        @after-leave="afterClose()"
      >
        <div
          v-show="isShown"
          class="base-modal-window-container-wrapper"
          :class="modalSettings.background"
          tabindex="-1"
          @mousedown.self="modalSettings.closeable && reject()"
        >
          <div
            ref="slot"
            v-trap-focus
            class="base-modal-window-container"
            role="dialog"
            aria-modal="true"
            :aria-label="modalSettings.ariaLabel"
            :aria-labelledby="modalSettings.ariaLabelledby"
            @keydown.escape="modalSettings.closeable && reject()"
          >
            <slot
              ref="slot"
              :resolve="resolve"
              :reject="reject"
              :close="close"
            />
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import vTrapFocus, { focusFirst } from '~/directives/trap-focus'

export interface ModalProps {
  modelValue?: boolean | null
  closeable?: boolean
  background?: 'black' | 'none'
  animation?: string
  closebutton?: boolean
  focus?: boolean
  to?: string
  ariaLabel?: string
  ariaLabelledby?: string
  history?: string
}
const props = withDefaults(defineProps<ModalProps>(), {
  modelValue: undefined,
  closeable: true,
  background: 'black',
  animation: 'fade',
  closebutton: true,
  focus: true,
  to: '#layer-modal',
  ariaLabel: null,
  ariaLabelledby: null,
})
let modalSettings: ModalProps = { ...props }

const emits = defineEmits([
  'update:modelValue',
  'before-enter',
  'after-enter',
  'after-close',
])

// isActiveInnerは、modelValueが定義されていない時にモーダルの開閉を制御する。
const isActiveInner = ref(false)

// isActiveはモーダルの開閉を制御する。modelValueが定義されていればそれを使い、定義されていなければisActiveInnerを使う
const isActive = computed(() => {
  if (props.modelValue != null) {
    return props.modelValue
  }
  return isActiveInner.value
})

// モーダルがマウントされているかを制御する。マウントはTeleportで行い、マウント時にはTransitionは無い
const isMount = ref(false)
// モーダルが表示されているかを制御する。表示時にはTransitionが起きる。
const isShown = ref(false)

watch(
  isActive,
  value => {
    if (value) {
      // 表示は、マウント後に動作するようにnextTickを利用
      isMount.value = true
      nextTick(() => {
        isShown.value = true
      })
    } else {
      // 非表示は、すぐに動作。Transitionが完了後、afterCloseでisMountがfalseになる
      isShown.value = false
    }
  },
  {
    immediate: true,
  },
)

let lastActiveElement: HTMLElement
const slot = ref(null)

const beforeEnter = () => {
  emits('before-enter')
}

const afterEnter = () => {
  // 最後のフォーカスを取得
  lastActiveElement = document.activeElement as HTMLElement
  // モーダル内にフォーカスを移動
  if (modalSettings.focus) {
    focusFirst(slot.value)
  }
  emits('after-enter')
}

const afterClose = () => {
  // フォーカスを復帰
  lastActiveElement.focus()
  emits('after-close')
  isMount.value = false
}

const close = () => {
  isActiveInner.value = false
  if (modalSettings.history) {
    closeModalWithHistory(modalSettings.history, reject)
  }
  emits('update:modelValue', false)
}

const open = () => {
  isActiveInner.value = true
  if (modalSettings.history) {
    showModalWithHistory(modalSettings.history, reject)
  }
  emits('update:modelValue', true)
}

// ***ProgramaticなOpen***
// 使い方
// 1.Modalを使いたいコンポーネント内に<BaseModal ref=modal>中身</BaseModal>を追加します
// 2.中身のコンポーネントで、propsないしはinjectしたresolve/rejectを呼びます。前者の場合には、モーダルの出力のオブジェクトを渡せます。
// 3.Modalを使いたいコンポーネント内の任意のasync関数内で、モーダルをオープンします。
// try {
//     const result = await modal.value.openAwait()
// } catch (e) {
// }

let tempResolve
let tempReject

function openAwait<T>(modalProps?: ModalProps): Promise<T> {
  if (modalProps) {
    modalSettings = { ...props, ...modalProps }
  }
  return new Promise<T>((resolve, reject) => {
    tempResolve = resolve
    tempReject = reject
    open()
  })
}

function resolve(payload?: any) {
  close()
  emits('update:modelValue', false)
  if (tempResolve) {
    tempResolve(payload)
    tempResolve = null
    tempReject = null
  }
}

function reject() {
  close()
  if (tempReject) {
    tempReject()
    tempResolve = null
    tempReject = null
  }
}

provide('resolve', resolve)
provide('reject', reject)
provide('close', close)
provide('open', open)
provide('openAwait', openAwait)

defineExpose({ open, openAwait, resolve, reject })

onBeforeUnmount(() => {
  if (modalSettings.history) {
    closeModalWithHistory(null, reject)
  }
})
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 100ms linear;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.base-modal {
}

.base-modal-window-container-wrapper {
  width: 100%;
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 0;

  &.none {
    background-color: none;
  }
  &.black {
    background-color: black;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    backdrop-filter: saturate(150%);
  }
}

.base-modal-window-container {
  height: fit-content;
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
